import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import SaveIcon from "@material-ui/icons/Save";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import VpnKeyIcon from "@material-ui/icons/VpnKey";
import { toast } from "react-toastify";
import api from "../../../services/api";
import toastError from "../../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },
  panel: {
    padding: theme.spacing(3),
    borderRadius: 8,
  },
  statusBox: {
    padding: theme.spacing(2),
    borderRadius: 8,
    background: theme.palette.action.hover,
    border: `1px solid ${theme.palette.divider}`,
  },
  actions: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },
}));

const LyraAISettings = () => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState({
    hasKey: false,
    maskedKey: null,
    model: "gpt-4o-mini",
  });
  const [apiKey, setApiKey] = useState("");
  const [model, setModel] = useState("gpt-4o-mini");

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/openai-settings");
      setStatus(data);
      setModel(data.model || "gpt-4o-mini");
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  const handleSave = async () => {
    setLoading(true);
    try {
      const payload = {
        model,
        ...(apiKey.trim() ? { apiKey: apiKey.trim() } : {}),
      };
      const { data } = await api.put("/openai-settings", payload);
      setStatus(data);
      setApiKey("");
      toast.success("Configuração da IA LYRA salva com sucesso.");
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveKey = async () => {
    setLoading(true);
    try {
      const { data } = await api.delete("/openai-settings/key");
      setStatus(data);
      setApiKey("");
      toast.success("Chave da OpenAI removida.");
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box className={classes.root}>
      <Paper variant="outlined" className={classes.panel}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h6">IA LYRA</Typography>
            <Typography variant="body2" color="textSecondary">
              Configure a chave da OpenAI usada pelos agentes individuais. A chave fica protegida no backend e nunca é exibida novamente.
            </Typography>
          </Grid>

          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              type="password"
              variant="outlined"
              label="Chave da OpenAI"
              placeholder={status.hasKey ? "Informe uma nova chave para substituir a atual" : "Cole a chave iniciada por sk-"}
              value={apiKey}
              onChange={(event) => setApiKey(event.target.value)}
              disabled={loading}
              InputProps={{
                startAdornment: <VpnKeyIcon style={{ marginRight: 8, color: "#777" }} />,
              }}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControl fullWidth variant="outlined">
              <InputLabel>Modelo</InputLabel>
              <Select
                value={model}
                onChange={(event) => setModel(event.target.value)}
                label="Modelo"
                disabled={loading}
              >
                <MenuItem value="gpt-4o-mini">gpt-4o-mini</MenuItem>
                <MenuItem value="gpt-4o">gpt-4o</MenuItem>
                <MenuItem value="gpt-4.1-mini">gpt-4.1-mini</MenuItem>
                <MenuItem value="gpt-4.1">gpt-4.1</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box className={classes.statusBox}>
              <Typography variant="subtitle2">Status</Typography>
              <Typography variant="body2">
                {status.hasKey ? `Chave configurada: ${status.maskedKey}` : "Nenhuma chave configurada."}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Modo de aprendizado: supervisionado, sem aplicar direto na LYRA.
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Box className={classes.actions}>
              <Button
                color="primary"
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={loading}
              >
                Salvar configuração
              </Button>
              <Button
                color="secondary"
                variant="outlined"
                startIcon={<DeleteOutlineIcon />}
                onClick={handleRemoveKey}
                disabled={loading || !status.hasKey}
              >
                Remover chave
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default LyraAISettings;
